// src/utils/achievementCalculations.js
import { INITIAL_RATING } from './ratingCalculations';

// 단복식 승리 판별 헬퍼
export function didPlayerWin(match, playerId) {
  if (match.match_type === 'doubles') {
    const isTeamA = match.player1_id === playerId || match.player2_id === playerId;
    return isTeamA
      ? match.winner_id === match.player1_id
      : match.winner_id === match.player3_id;
  }
  return match.winner_id === playerId;
}

// 희귀도 설정
export const RARITY_CONFIG = {
  normal:    { label: 'Normal',    color: 'text-gray-400',   border: 'border-gray-400/40',   bgLabel: 'bg-gray-400/15',   sortOrder: 0 },
  rare:      { label: 'Rare',      color: 'text-blue-400',   border: 'border-blue-400/60',   bgLabel: 'bg-blue-400/15',   sortOrder: 1 },
  epic:      { label: 'Epic',      color: 'text-purple-400', border: 'border-purple-400/60', bgLabel: 'bg-purple-400/15', sortOrder: 2 },
  legendary: { label: 'Legendary', color: 'text-amber-400',  border: 'border-amber-400/60',  bgLabel: 'bg-amber-400/15',  sortOrder: 3 },
};

export const ACHIEVEMENTS = [
  // 연승
  {
    id: 'win_streak_3',
    label: '3연승',
    description: '연속 3승 달성',
    icon: 'Flame',
    color: 'text-orange-400',
    bgColor: 'bg-orange-400/10',
    borderColor: 'border-orange-400/20',
    rarity: 'normal',
  },
  {
    id: 'win_streak_5',
    label: '5연승',
    description: '연속 5승 달성',
    icon: 'Flame',
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-500/20',
    rarity: 'rare',
  },
  {
    id: 'win_streak_10',
    label: '10연승',
    description: '연속 10승 달성',
    icon: 'Flame',
    color: 'text-amber-400',
    bgColor: 'bg-amber-400/10',
    borderColor: 'border-amber-400/20',
    rarity: 'epic',
  },
  // 레이팅
  {
    id: 'rating_climb_100',
    label: 'Elo +100',
    description: '초기 대비 Elo 100점 이상 상승',
    icon: 'TrendingUp',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-400/10',
    borderColor: 'border-emerald-400/20',
    rarity: 'rare',
  },
  {
    id: 'season_high',
    label: '시즌 최고',
    description: '역대 최고 레이팅 갱신 중',
    icon: 'Crown',
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-400/10',
    borderColor: 'border-yellow-400/20',
    rarity: 'epic',
  },
  // 경기 수
  {
    id: 'matches_10',
    label: '10경기',
    description: '공식 10경기 달성',
    icon: 'Target',
    color: 'text-sky-400',
    bgColor: 'bg-sky-400/10',
    borderColor: 'border-sky-400/20',
    rarity: 'normal',
  },
  {
    id: 'matches_30',
    label: '30경기',
    description: '공식 30경기 달성',
    icon: 'Target',
    color: 'text-blue-400',
    bgColor: 'bg-blue-400/10',
    borderColor: 'border-blue-400/20',
    rarity: 'rare',
  },
  {
    id: 'matches_50',
    label: '50경기',
    description: '공식 50경기 달성',
    icon: 'Target',
    color: 'text-indigo-400',
    bgColor: 'bg-indigo-400/10',
    borderColor: 'border-indigo-400/20',
    rarity: 'epic',
  },
  // 업셋
  {
    id: 'giant_killer',
    label: '자이언트 킬',
    description: 'Elo 100점 이상 높은 상대를 격파',
    icon: 'Zap',
    color: 'text-purple-400',
    bgColor: 'bg-purple-400/10',
    borderColor: 'border-purple-400/20',
    rarity: 'rare',
  },
  // 복식
  {
    id: 'doubles_10',
    label: '복식 10경기',
    description: '복식 10경기 이상 참가',
    icon: 'UsersRound',
    color: 'text-teal-400',
    bgColor: 'bg-teal-400/10',
    borderColor: 'border-teal-400/20',
    rarity: 'normal',
  },
  // 출석률 (랭킹전 참여 주 수 기준)
  {
    id: 'attendance_3',
    label: '3주 출석',
    description: '랭킹전 3주 이상 참가',
    icon: 'CalendarCheck',
    color: 'text-lime-400',
    bgColor: 'bg-lime-400/10',
    borderColor: 'border-lime-400/20',
    rarity: 'normal',
  },
  {
    id: 'attendance_8',
    label: '8주 출석',
    description: '랭킹전 8주 이상 참가',
    icon: 'CalendarCheck',
    color: 'text-green-400',
    bgColor: 'bg-green-400/10',
    borderColor: 'border-green-400/20',
    rarity: 'rare',
  },
  {
    id: 'attendance_15',
    label: '15주 출석',
    description: '랭킹전 15주 이상 참가',
    icon: 'CalendarCheck',
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/20',
    rarity: 'epic',
  },
];

const ACHIEVEMENT_MAP = {};
ACHIEVEMENTS.forEach(a => { ACHIEVEMENT_MAP[a.id] = a; });

export function computePlayerAchievements(player, matches) {
  const earned = [];
  const playerId = player.id;

  // 이 선수의 공식 경기만 시간순 정렬
  const playerMatches = matches
    .filter(m => m.match_category !== 'friendly')
    .filter(m =>
      m.player1_id === playerId || m.player2_id === playerId ||
      m.player3_id === playerId || m.player4_id === playerId
    )
    .sort((a, b) =>
      new Date(a.date) - new Date(b.date) ||
      new Date(a.created_at) - new Date(b.created_at)
    );

  // --- 연승 ---
  let maxStreak = 0;
  let currentStreak = 0;
  for (const m of playerMatches) {
    if (didPlayerWin(m, playerId)) {
      currentStreak++;
      if (currentStreak > maxStreak) maxStreak = currentStreak;
    } else {
      currentStreak = 0;
    }
  }
  if (maxStreak >= 10) earned.push(ACHIEVEMENT_MAP.win_streak_10);
  else if (maxStreak >= 5) earned.push(ACHIEVEMENT_MAP.win_streak_5);
  else if (maxStreak >= 3) earned.push(ACHIEVEMENT_MAP.win_streak_3);

  // --- 레이팅 상승 ---
  const history = player.rating_history || [];
  const currentRating = player.current_rating || INITIAL_RATING;

  if (currentRating - INITIAL_RATING >= 100) {
    earned.push(ACHIEVEMENT_MAP.rating_climb_100);
  }

  if (history.length >= 5) {
    const maxRating = Math.max(...history.map(h => h.rating));
    if (currentRating >= maxRating) {
      earned.push(ACHIEVEMENT_MAP.season_high);
    }
  }

  // --- 경기 수 ---
  const totalOfficial = player.total_matches || 0;
  if (totalOfficial >= 50) earned.push(ACHIEVEMENT_MAP.matches_50);
  else if (totalOfficial >= 30) earned.push(ACHIEVEMENT_MAP.matches_30);
  else if (totalOfficial >= 10) earned.push(ACHIEVEMENT_MAP.matches_10);

  // --- 업셋 (단식, rating_changes 기반) ---
  const singlesMatches = playerMatches.filter(m => m.match_type !== 'doubles');
  for (const m of singlesMatches) {
    if (m.winner_id !== playerId) continue;
    const opponentId = m.player1_id === playerId ? m.player2_id : m.player1_id;
    const playerBefore = m.rating_changes?.[playerId]?.before;
    const opponentBefore = m.rating_changes?.[opponentId]?.before;
    if (playerBefore && opponentBefore && opponentBefore - playerBefore >= 100) {
      earned.push(ACHIEVEMENT_MAP.giant_killer);
      break;
    }
  }

  // --- 복식 경기 수 ---
  const doublesCount = playerMatches.filter(m => m.match_type === 'doubles').length;
  if (doublesCount >= 10) {
    earned.push(ACHIEVEMENT_MAP.doubles_10);
  }

  // --- 출석률 (랭킹전 참여 주 수) ---
  // 공식 경기 날짜를 주 단위로 그룹핑 (같은 주에 여러 경기 = 1주 출석)
  const weekSet = new Set();
  for (const m of playerMatches) {
    if (m.date) {
      const d = new Date(m.date);
      // ISO 주 기준: 해당 날짜가 속한 주의 월요일 날짜를 키로 사용
      const day = d.getDay();
      const diff = d.getDate() - day + (day === 0 ? -6 : 1); // 월요일
      const monday = new Date(d);
      monday.setDate(diff);
      weekSet.add(monday.toISOString().split('T')[0]);
    }
  }
  const weeksAttended = weekSet.size;
  if (weeksAttended >= 15) earned.push(ACHIEVEMENT_MAP.attendance_15);
  else if (weeksAttended >= 8) earned.push(ACHIEVEMENT_MAP.attendance_8);
  else if (weeksAttended >= 3) earned.push(ACHIEVEMENT_MAP.attendance_3);

  return earned;
}

// 전체 선수의 뱃지 획득 현황 집계
export function computeAllPlayersAchievements(players, matches) {
  const badgeMap = {};
  ACHIEVEMENTS.forEach(a => { badgeMap[a.id] = { badge: a, players: [] }; });

  (players || []).forEach(player => {
    const earned = computePlayerAchievements(player, matches);
    earned.forEach(a => {
      if (badgeMap[a.id]) {
        badgeMap[a.id].players.push({ id: player.id, name: player.name });
      }
    });
  });

  return badgeMap;
}

// 선수의 대표 뱃지 1개 (가장 높은 희귀도)
export function getRepresentativeBadge(player, matches) {
  const earned = computePlayerAchievements(player, matches);
  if (earned.length === 0) return null;

  return earned.reduce((best, curr) => {
    const bestOrder = RARITY_CONFIG[best.rarity]?.sortOrder ?? 0;
    const currOrder = RARITY_CONFIG[curr.rarity]?.sortOrder ?? 0;
    return currOrder > bestOrder ? curr : best;
  });
}
